import type { ErrorEvent, Exception } from "@similarweb/sentry-logger";

import { I18N_INIT_SENTRY_MESSAGE, RESOURCE_FETCH_SENTRY_MESSAGE } from "app/sentry/constants";

const HEADLESS_CHROME_PATTERN = /HeadlessChrome/i;
const INTEGRATION_ENVIRONMENT = "Integration";

export const getException = (event: ErrorEvent | null): Exception | undefined => {
    if (event === null) {
        return undefined;
    }

    return event.exception?.values?.[0];
};

export const getExceptionMessage = (event: ErrorEvent | null): string => {
    const exception = getException(event);
    return event?.message || exception?.value || "";
};

const containsHeadlessChrome = (value: string): boolean => HEADLESS_CHROME_PATTERN.test(value);

/**
 * Event contexts/tags are often empty at `beforeSend`; when they are, this falls back to
 * `navigator.userAgent` and returns `"HeadlessChrome"` when the UA matches automation browsers.
 */
export const getBrowserNameFromEvent = (event: ErrorEvent | null): string => {
    if (!event) {
        return "";
    }

    const fromEvent =
        (typeof event.contexts?.browser?.name === "string" ? event.contexts.browser.name : "") ||
        (typeof event.tags?.browser === "string" ? event.tags.browser : "") ||
        (typeof event.tags?.["browser.name"] === "string" ? event.tags["browser.name"] : "");

    if (fromEvent) {
        return fromEvent;
    }

    if (typeof navigator !== "undefined" && containsHeadlessChrome(navigator.userAgent)) {
        return "HeadlessChrome";
    }

    return "";
};

const getEnvironment = (event: ErrorEvent | null): string => {
    if (!event) {
        return "";
    }
    return event.environment || (typeof event.tags?.environment === "string" ? event.tags.environment : "") || "";
};

const isI18nInitMessage = (message: string): boolean => message.includes(I18N_INIT_SENTRY_MESSAGE);

/**
 * True when the event originates from an automation (HeadlessChrome) browser running
 * against an Integration (sandbox) environment.
 */
export const isAutomationIntegrationEvent = (event: ErrorEvent | null): boolean => {
    const browserName = getBrowserNameFromEvent(event);
    const environment = getEnvironment(event);

    return containsHeadlessChrome(browserName) && environment === INTEGRATION_ENVIRONMENT;
};

export const isI18nInitAutomationNoise = (event: ErrorEvent | null): boolean => {
    if (!event) {
        return false;
    }

    if (!isI18nInitMessage(getExceptionMessage(event))) {
        return false;
    }

    return isAutomationIntegrationEvent(event);
};

/**
 * Filters "Failed to read response text from <resource>" events (PRO-FRONTEND-JPN9, PRO-FRONTEND-JKF4)
 * raised when headless automation runs on sandboxes abort static-resource fetches mid-read.
 * The error is already gracefully handled with a fallback value, so it's noise in automation runs.
 */
export const isResourceFetchAutomationNoise = (event: ErrorEvent | null): boolean => {
    if (!event) {
        return false;
    }

    if (!getExceptionMessage(event).includes(RESOURCE_FETCH_SENTRY_MESSAGE)) {
        return false;
    }

    return isAutomationIntegrationEvent(event);
};
